import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Avatar,
  CircularProgress,
  LinearProgress,
  Snackbar,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  Divider,
  Alert,
  InputAdornment,
} from '@mui/material';
import { restaurantService } from '../../services/restaurantService';
import {
  Save,
  Refresh,
  Upload,
  Business,
  Phone,
  Email,
  Language,
  Palette,
  Image,
  Close,
  PhotoCamera,
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import SliderManagement from './SliderManagement';

const RestaurantConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ logo: false, cover: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { showSuccess, showError } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadRestaurantConfig();
    console.log("Data = ", config)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRestaurantConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await restaurantService.getRestaurantConfig();
      console.log('Loaded restaurant config:', data);
      setConfig({
        ...data,
        theme_config: data?.theme_config || {
          primary_color: '#1976d2',
          secondary_color: '#dc004e',
          accent_color: '#ff9800',
        },
      });
    } catch (err) {
      setError('Failed to load restaurant configuration');
      showError('Failed to load restaurant configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    console.log("Config = ", config)
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      await restaurantService.updateRestaurantConfig(config);
      setSuccess('Restaurant configuration updated successfully!');
      showSuccess('Restaurant configuration updated successfully!');
      
      // Reload config to get updated data
      await loadRestaurantConfig();
    } catch (err) {
      setError('Failed to update restaurant configuration');
      showError('Failed to update restaurant configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => (prev ? {
      ...prev,
      [field]: value
    } : null));
  };

  const handleThemeChange = (colorField, value) => {
    setConfig(prev => (prev ? {
      ...prev,
      theme_config: {
        ...(prev.theme_config || {}),
        [colorField]: value
      }
    } : null));
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    const imageType = field === 'logo_url' ? 'logo' : 'cover';
    try {
      setUploading(prev => ({ ...prev, [imageType]: true }));
      setError('');
      const result = await restaurantService.uploadRestaurantImage(imageType, file);
      
      console.log('Upload result:', result);
      
      // Update config with the full image URL returned by backend
      setConfig(prev => {
        const updated = {
          ...prev,
          [field]: result.image_url
        };
        console.log('Updated config:', updated);
        return updated;
      });
      
      showSuccess(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} uploaded successfully!`);
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || `Failed to upload ${imageType}`;
      setError(errorMsg);
      showError(errorMsg);
    } finally {
      setUploading(prev => ({ ...prev, [imageType]: false }));
    }
  };

  if (loading || !config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 200px)">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        mb: { xs: 3, sm: 4 }, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2
      }}>
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight="bold" 
            gutterBottom
          >
            Restaurant Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your restaurant's branding and settings. Changes will reflect on the public website.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadRestaurantConfig}
            disabled={loading}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Basic Information */}
        <Grid item xs={12} lg={8}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="primary" />
                Basic Information
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />
              
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Restaurant Name"
                    value={config?.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    margin="none"
                    required
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={config?.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    margin="none"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color="action" />
                        </InputAdornment>
                      ),
                    }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={config?.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    margin="none"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={config?.website || ''}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    margin="none"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Language color="action" />
                        </InputAdornment>
                      ),
                    }}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={config?.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    margin="none"
                    multiline
                    rows={isMobile ? 4 : 3}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Logo and Images */}
        <Grid item xs={12} lg={4}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Image color="primary" />
                Branding
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />
              
              {/* Logo Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Restaurant Logo
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  alignItems: 'center', 
                  gap: 2 
                }}>
                  <Avatar
                    src={config?.logo_url || undefined}
                    alt="Restaurant Logo"
                    sx={{ 
                      width: { xs: 80, sm: 80 }, 
                      height: { xs: 80, sm: 80 },
                      mx: { xs: 'auto', sm: 0 }
                    }}
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="logo-upload"
                      type="file"
                      onChange={(e) => handleImageUpload('logo_url', e.target.files[0])}
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCamera />}
                        disabled={uploading.logo}
                        fullWidth={isMobile}
                        size={isMobile ? "small" : "medium"}
                      >
                        {uploading.logo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    </label>
                    {uploading.logo && (
                      <LinearProgress sx={{ mt: 1 }} />
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Cover Image Upload */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Cover Image
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' }, 
                  alignItems: 'center', 
                  gap: 2 
                }}>
                  <Avatar
                    src={config?.cover_image_url || undefined}
                    alt="Restaurant Cover"
                    sx={{ 
                      width: { xs: 120, sm: 120 }, 
                      height: { xs: 80, sm: 80 },
                      mx: { xs: 'auto', sm: 0 }
                    }}
                    variant="rounded"
                  />
                  <Box sx={{ flexGrow: 1 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="cover-upload"
                      type="file"
                      onChange={(e) => handleImageUpload('cover_image_url', e.target.files[0])}
                    />
                    <label htmlFor="cover-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCamera />}
                        disabled={uploading.cover}
                        fullWidth={isMobile}
                        size={isMobile ? "small" : "medium"}
                      >
                        {uploading.cover ? 'Uploading...' : 'Upload Cover Image'}
                      </Button>
                    </label>
                    {uploading.cover && (
                      <LinearProgress sx={{ mt: 1 }} />
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Theme Configuration */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Palette color="primary" />
                Theme Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Customize the color scheme for your public website
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />
              
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Primary Color"
                    type="color"
                    value={config?.theme_config?.primary_color || '#000000'}
                    onChange={(e) => handleThemeChange('primary_color', e.target.value)}
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& input[type="color"]': { p: 1, height: 40 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Secondary Color"
                    type="color"
                    value={config?.theme_config?.secondary_color || '#000000'}
                    onChange={(e) => handleThemeChange('secondary_color', e.target.value)}
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& input[type="color"]': { p: 1, height: 40 } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Accent Color"
                    type="color"
                    value={config?.theme_config?.accent_color || '#000000'}
                    onChange={(e) => handleThemeChange('accent_color', e.target.value)}
                    margin="none"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& input[type="color"]': { p: 1, height: 40 } }}
                  />
                </Grid>
              </Grid>

              {/* Color Preview */}
              <Box sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Color Preview
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: config?.theme_config?.primary_color,
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Typography variant="body2">Primary</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: config?.theme_config?.secondary_color,
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Typography variant="body2">Secondary</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: config?.theme_config?.accent_color,
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Typography variant="body2">Accent</Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Slider Images Management */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Image color="primary" />
                Hero Slider Images
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Manage slider images for the homepage hero section. Upload multiple images to create an engaging carousel.
              </Typography>
              <Divider sx={{ my: 2, mb: 3 }} />
              
              <SliderManagement />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RestaurantConfig;
